import React, { useEffect, useState } from "react";
import { Box, TextField, Typography, Button, IconButton, Icon, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip } from "@mui/material";
import CustomMarkdown from "../CustomMarkdown";
import { useAppDispatch, useAppSelector } from 'src/store';
import { deleteMessage, deliverMessageNow, updateMessage } from "src/features/account/messages.slice";
import { decrementMessageCount, addMessage } from "src/features/account/conversations.slice";
import { useSnackbar } from "notistack";
import { getBranding } from "src/utils/branding";
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from 'src/theme/ThemeProvider';
import { alpha } from '@mui/material/styles';
import DraftsIcon from '@mui/icons-material/Drafts';

// Import the new components
import ConversationCard from "./ConversationCard";
import FunctionMessage from './Messages/FunctionMessage';
import MessageOptions from './Messages/MessageOptions';
import MessageProgress from './Messages/MessageProgress';
import MessageReadReceipts from './Messages/MessageReadReceipts';
import EmailParticipants from './Messages/EmailParticipants';
import ClickableContactName from '../common/ClickableContactName';
import MessageMetadataDebug from './Messages/MessageMetadataDebug';

interface MessageProps {
  message: any;
  sendingEntity: any;
  channelId: any;
  fetchConversationsWithMessagesAction?: () => void;
  conversationDeliveryState?: string;
  variant?: string;
  isOptimistic?: boolean;
  parentConversation?: any;
}

const Message: React.FC<MessageProps> = ({
  message,
  sendingEntity,
  channelId,
  fetchConversationsWithMessagesAction,
  conversationDeliveryState = '',
  variant = 'full',
  isOptimistic = false,
  parentConversation
}) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(true);
  const [updatedMessage, setUpdatedMessage] = useState(message.content);
  const [currentMessageId, setCurrentMessageId] = useState(null);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.user);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [conversation, setConversation] = useState(null);
  const [deliverNowButtonDisabled, setDeliverNowButtonDisabled] = useState(false);
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDarkMode = mode === 'dark';
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // Check for optimistic message properties
  const isOptimisticMessage = message.isOptimistic || false;
  const optimisticStatus = isOptimisticMessage ? message.status : null;

  useEffect(() => {
    const extractJson = (text: string) => {
      if (!text) return null;
      const jsonPattern = /{"conversation"=>.*?}}/;
      const match = text.match(jsonPattern);
      return match ? match[0] : null;
    };

    const jsonStr = extractJson(message.raw_content);
    if (jsonStr) {
      try {
        const parsedData = JSON.parse(jsonStr.replace(/=>/g, ':').replace(/nil/g, 'null') || '{}');
        setConversation(parsedData.conversation);
      } catch (error) {
        console.error('Failed to parse conversation JSON:', error);
      }
    }
  }, [message.raw_content]);

  const setSuccessMessage = (msg: string) => {
    // enqueueSnackbar(msg, {
    //   variant: 'success',
    //   autoHideDuration: 3000
    // });
  };

  const setErrorMessage = (msg: string) => {
    enqueueSnackbar(msg, {
      variant: 'error',
      autoHideDuration: 3000
    });
  };

  const editMessage = () => {
    setLoading(true);
    setCurrentMessageId(message.id);
    setShowEdit(false);
    const properChannelId = channelId || message.sender.channel_id;
    dispatch(updateMessage({
      messageId: message.id,
      content: updatedMessage,
      raw_content: updatedMessage,
      channelId: properChannelId,
    })).unwrap()
      .then((response: any) => {
        setSuccessMessage(response.message);
      }).catch((error: any) => {
        setErrorMessage(error);
      }).finally(() => {
        setLoading(false);
      })
  };

  const handleDelete = () => {
    setLoading(true);
    dispatch(deleteMessage({ messageId: message.id, channelId: channelId, conversationId: message.conversation_id }));
    dispatch(decrementMessageCount({ conversationId: message.conversation_id }));
    setLoading(false);
  };

  const handleSendNow = () => {
    // Directly confirm and deliver message without extra dialog
    confirmSendNow();
  };

  const confirmSendNow = () => {
    setLoading(true);
    setCurrentMessageId(message.id);
    setDeliverNowButtonDisabled(true);
    setConfirmOpen(false);
    dispatch(deliverMessageNow({ messageId: message.id, channelId: channelId }))
      .unwrap()
      .then((response: any) => {
        setSuccessMessage(response.message);
        
        // Update the message in the Redux store to reflect delivery
        if (response && response.message) {
          const updatedMessage = {
            ...response.message,
            status: 'delivered',
            delivered_at: response.message.deliver_after || new Date().toISOString()
          };
          dispatch(addMessage(updatedMessage));
        }
      }).catch((error: any) => {
        setErrorMessage(error);
      }).finally(() => {
        setLoading(false);
      })
  };

  const handlePlaygroundNavigate = () => {
    navigate(`/playground?message_id=${message.id}`);
  };

  const isSelf = sendingEntity && sendingEntity.role === message.sender_role;
  const isDraftMessage = (message.status !== 'delivered' && message.status !== 'sent' && !isOptimisticMessage);
  const isConversationContext = variant === 'compact';
  const showDotted = isConversationContext && isDraftMessage;
  const dashedColor = alpha(theme.palette.text.secondary, 0.22);

  // Find the contact_id for the message sender
  const senderContactId = parentConversation?.participating_entities?.find(
    (entity: any) => entity.id === message.sender_id && entity.role === message.sender_role
  )?.contact_id;

  // ** Handle function messages separately **
  if (message.sender_role === "function") {
    return <FunctionMessage message={message} channelId={channelId} conversationDeliveryState={conversationDeliveryState} />;
  }

  return (
    <Box
      key={message.id}
      sx={{
        mb: message.sender_role === "function" ? 0.5 : 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        opacity: isOptimisticMessage ? 0.85 : 1,
        position: "relative",
        alignItems: isSelf ? "flex-end" : "flex-start",
        px: { xs: 1, sm: 0 },
        '&:hover .action-buttons, &:hover .timestamp': {
          opacity: 0.5
        }
      }}
      data-message-role={message.sender_role}
    >
      {/* Debug metadata for admin users (role 11) */}
      {user?.role === 11 && (
        <Box sx={{ 
          position: 'relative',
          mb: showDebug ? 2 : 0,
        }}>
          {showDebug && 
            <Box>
              <Typography>
                created_at:{message.created_at}
              </Typography>
              <Typography>
                updated_at:{message.updated_at}
              </Typography>
              <Typography>
                deliver_after:{message.deliver_after}
              </Typography>
              <Typography>
                delivered_at:{message.delivered_at}
              </Typography>
              <Typography>
                sort_time_used:{message.sort_time_used}
              </Typography>
            </Box>
          }
          {/* Collapsed view with toggle button */}
          {!showDebug && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end',
              mb: 1,
              gap: 1,
            }}>
              {/* Show token count when collapsed */}
              {message.metadata?.consumption && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    opacity: 0.5,
                    fontSize: '11px',
                  }}
                >
                  {(() => {
                    const consumption = message.metadata.consumption || {};
                    const totalTokens = Object.values(consumption).reduce(
                      (sum: number, step: any) => sum + (step?.input_tokens || 0) + (step?.output_tokens || 0), 0
                    ) as number;
                    return `${totalTokens.toLocaleString()} tokens`;
                  })()}
                </Typography>
              )}
              <IconButton
                size="small"
                onClick={() => setShowDebug(true)}
                sx={{
                  opacity: 0.3,
                  transition: 'opacity 0.3s',
                  '&:hover': { opacity: 0.8 },
                  padding: '4px',
                }}
              >
                <Icon sx={{ fontSize: '16px' }}>code</Icon>
              </IconButton>
            </Box>
          )}
          
          {/* Expanded debug view */}
          {showDebug && (
            <Box sx={{ position: 'relative' }}>
              <IconButton
                size="small"
                onClick={() => setShowDebug(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': { 
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <Icon sx={{ fontSize: '16px' }}>close</Icon>
              </IconButton>
              <MessageMetadataDebug message={message} />
            </Box>
          )}
        </Box>
      )}
      
      <Box sx={{
        display: "flex",
        flexDirection: "row",
        gap: "4px",
        width: "90%",
        maxWidth: "90%",
        justifyContent: isSelf ? "flex-end" : "flex-start",
        alignItems: "center",
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {message.sender_role === 'user' && senderContactId ? (
            <ClickableContactName
              contactId={senderContactId}
              contactName={message.sender_name}
              variant="subtitle2"
              sx={{
                fontSize: "14px",
                textTransform: "capitalize",
                color: isDarkMode ? 'text.secondary' : 'rgba(0, 0, 0, 0.6)',
                mr: '4px',
                ml: '4px',
              }}
            />
          ) : (
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: "14px",
                textTransform: "capitalize",
                color: isDarkMode ? 'text.secondary' : 'rgba(0, 0, 0, 0.6)',
                mr: '4px',
                ml: '4px',
              }}
            >
              {message.sender_name}
            </Typography>
          )}
          {message.sender_role === 'assistant' ? (
            <IconButton
              size="small"
              onClick={() => channelId && navigate(`/channels?channel_id=${channelId}`)}
              disabled={!channelId}
              sx={{
                p: 0.5,
                mt: -1,
                ml: 1,
                borderRadius: '50%',
                bgcolor: theme => alpha(theme.palette.primary.main, isDarkMode ? 0.2 : 0.1),
                '&:hover': {
                  bgcolor: theme => alpha(theme.palette.primary.main, isDarkMode ? 0.3 : 0.2),
                },
              }}
            >
              <Box
                component="img"
                src={getBranding('oppy_expressions_no_mouth')}
                alt="Assistant Icon"
                sx={{
                  width: 20,
                  height: 20,
                  filter: isDarkMode ? 'drop-shadow(0 0 4px rgba(255,255,255,0.6))' : 'none',
                }}
              />
            </IconButton>
          ) : message.sender_role === 'user' ? (
            <Icon sx={{ fontSize: '16px', ml: .5 }}>person</Icon>
          ) : (
            <Icon sx={{ fontSize: '18px', ml: .5 }}>computer</Icon>
          )}
        </Box>
        {/* Add draft indicator for unsent messages */}
        {message.status !== 'delivered' && message.status !== 'sent' && !isOptimisticMessage && (
          <Tooltip title="Draft - This message hasn't been sent yet" arrow placement="top">
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <DraftsIcon 
                sx={{ 
                  fontSize: 16, 
                  color: theme.palette.warning.main,
                  opacity: 0.8
                }} 
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  ml: 0.5, 
                  color: theme.palette.warning.main,
                  fontWeight: 500,
                  opacity: 0.8
                }}
              >
                Draft
              </Typography>
            </Box>
          </Tooltip>
        )}
        {/* Add read receipts for assistant messages */}
        {message.sender_role === 'assistant' && (
          <MessageReadReceipts 
            status={message.status}
            deliveredAt={message.delivered_at}
            metadata={message.metadata}
          />
        )}
        {/* Add email participants if present in metadata */}
        {message.metadata?.email_participants && (
          <EmailParticipants
            messageId={message.id}
            channelId={channelId}
            conversationId={message.conversation_id}
            emailParticipants={message.metadata.email_participants}
            messageMetadata={message.metadata}
          />
        )}
      </Box>
      {showEdit ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-end",
            gap: "10px",
            scrollbarWidth: "none",
            maxWidth: "100%",
            minWidth: "300px",
          }}
        >
          <TextField
            value={updatedMessage}
            InputLabelProps={{ shrink: false }}
            onChange={(e) => setUpdatedMessage(e.target.value)}
            defaultValue={message.content}
            multiline
            maxRows={8}
            variant="outlined"
            sx={{
              width: '100%',
              maxWidth: '90%',
              '& .MuiInputBase-inputMultiline': {
                scrollbarWidth: 'none',
                'msOverflowStyle': 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
                '& fieldset': {
                  borderColor: isDarkMode ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
                },
              },
              '& .MuiInputBase-input': {
                color: isDarkMode ? theme.palette.text.primary : theme.palette.text.primary,
              },
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "10px" }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowEdit(false);
                setUpdatedMessage(message.content);
              }}
              sx={{
                color: isDarkMode ? theme.palette.text.primary : undefined,
                borderColor: isDarkMode ? alpha(theme.palette.divider, 0.1) : undefined,
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={editMessage}>
              Update
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: 'relative',
              overflow: 'visible',
              width: '100%',
              maxWidth: '100%',
              minHeight: '75px',
            }}
          >
            <Box
              sx={{
                marginTop: 1,
                padding: { xs: "12px 16px", sm: "16px 24px" },
                borderRadius: "8px",
                color: theme.palette.text.primary,
                backgroundColor: showDotted ? 'transparent' : (isSelf ? theme.palette.message.self : theme.palette.message.other),
                border: showDotted ? `1.5px dashed ${dashedColor}` : 'none',
                wordWrap: "break-word",
                overflowWrap: "anywhere",
                transition: "300ms ease-in-out",
                display: "flex",
                flexDirection: "column",
                '& > *': {
                  margin: 0,
                  padding: 0,
                  overflowWrap: 'anywhere',
                },
                '& img': {
                  maxWidth: '50%',
                  display: 'block',
                  margin: '0 auto',
                  borderRadius: "8px",
                  transition: "300ms ease-in-out",
                  mb: 2,
                },
                '& img:hover': {
                  maxWidth: '90%',
                  transition: "300ms ease-in-out",
                },
                borderLeft: loading && !showDotted ? `6px solid ${theme.palette.primary.main}` : undefined,
                '@keyframes fadeInOut': {
                  '0%, 100%': { borderLeftColor: 'transparent' },
                  '50%': { borderLeftColor: theme.palette.primary.main },
                },
                animation: loading ? 'fadeInOut 1s infinite' : 'none',
              }}
            >
              <Typography variant="body2"
                sx={{
                  '& a': {
                    color: "inherit",
                    textDecoration: "underline"
                  },
                  opacity: message.status === "delivered" || message.status === "sent" ? 1 : 0.7
                }}
              >
                {conversation ? (
                  <ConversationCard
                    conversation={conversation}
                    handleConversationsListToggleClick={undefined}
                  />
                ) : null}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  opacity: message.status === "delivered" || message.status === "sent" ? 1 : 0.7,
                  '& a': {
                    color: "inherit",
                    textDecoration: "underline"
                  },
                  '& p': {
                    margin: '0.5em 0'
                  }
                }}
              >
                <CustomMarkdown>{message.content}</CustomMarkdown>
              </Typography>
              {message.metadata?.progress !== undefined && !isNaN(Number(message.metadata.progress)) && (
                <MessageProgress progress={Number(message.metadata.progress)} />
              )}
              {(message.status === "undelivered" || message.metadata?.ErrorCode) && (
                <Tooltip
                  title={
                    message.metadata?.ErrorCode === '30034'
                      ? "Messages sent to US numbers will not be delivered if they are sent from numbers that are not associated with an approved A2P 10DLC Campaign."
                      : message.metadata?.ErrorCode === '30024'
                        ? "If this is for US A2P 10DLC and you just registered this number, it could take a brief period of time for all carriers to provision the number to your campaign. Typically you would see all messages towards a single carrier fail, if the code isn't provisioned."
                        : message.metadata?.ErrorCode || ""
                  }
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      color: "red",
                      mb: -1,
                      alignSelf: "flex-start",
                    }}
                  >
                    undelivered
                  </Typography>
                </Tooltip>
              )}
            </Box>

            {/* Replace action buttons with MessageOptions component */}
            {showActionButtons && (
              <MessageOptions
                message={message}
                onEdit={() => setShowEdit(true)}
                onDelete={handleDelete}
                onSendNow={handleSendNow}
                onPlayground={handlePlaygroundNavigate}
                showPlayground={user.role === 11}
                messageDelivered={message.status === "delivered" || message.status === "sent"}
                channelId={channelId}
                conversationDeliveryState={conversationDeliveryState}
              />
            )}


            {isOptimisticMessage && (
              <Chip
                label={optimisticStatus === 'sending' ? "Sending..." : "Failed"}
                size="small"
                color={optimisticStatus === 'sending' ? "primary" : "error"}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  position: 'absolute',
                  top: '-12px',
                  right: isSelf ? 0 : 'auto',
                  left: isSelf ? 'auto' : 0,
                }}
              />
            )}
          </Box>
        </>
      )}

      {/* Dialog for confirming send now */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Message Delivery</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to deliver this message now?
          </DialogContentText>
          <Typography
            variant="body2"
            component="div"
            sx={{
              mt: 2,
              p: 1,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1,
              maxHeight: 200,
              overflow: 'auto'
            }}
          >
            <CustomMarkdown>{message.content}</CustomMarkdown>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmSendNow}
            autoFocus
            variant="contained"
          >
            Deliver Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default Message;
